import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Server,
  Radio,
  Globe,
  AlertTriangle,
  TrendingUp,
  Zap,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

export default function AnalyticsDashboardPage() {
  const { api } = useAuth();
  const [period, setPeriod] = useState("today");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data states
  const [liveQPS, setLiveQPS] = useState(null);
  const [regionalQPS, setRegionalQPS] = useState([]);
  const [sspQPS, setSspQPS] = useState([]);
  const [dspQPS, setDspQPS] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [timeseries, setTimeseries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch sequentially to avoid overloading MongoDB
      const liveRes = await api.get("/qps/live");
      setLiveQPS(liveRes.data);

      const summaryRes = await api.get(`/qps/summary?period=${period}`);
      setSummary(summaryRes.data);

      const timeseriesRes = await api.get(`/qps/timeseries?period=${period}`);
      setTimeseries(timeseriesRes.data);

      // These are lighter — can run in parallel
      const [regionalRes, sspRes, dspRes, conflictsRes] = await Promise.all([
        api.get("/qps/regional").catch(() => ({ data: [] })),
        api.get(`/qps/by-ssp?period=${period}`).catch(() => ({ data: [] })),
        api.get(`/qps/by-dsp?period=${period}`).catch(() => ({ data: [] })),
        api.get("/qps/conflicts").catch(() => ({ data: [] })),
      ]);
      
      setRegionalQPS(regionalRes.data);
      setSspQPS(sspRes.data);
      setDspQPS(dspRes.data);
      setConflicts(conflictsRes.data);
      setDataLoaded(true);
    } catch (error) {
      toast.error("Failed to fetch analytics data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, period]);

  // Don't auto-fetch on mount — wait for Generate click
  
  const handleRefresh = () => {
    setRefreshing(true);
    fetchAllData();
  };

  const formatQPS = (value) => {
    if (!value && value !== 0) return "0";
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    if (value >= 1) {
      return value.toFixed(1);
    }
    // Show more decimal places for small QPS values
    if (value >= 0.01) {
      return value.toFixed(2);
    }
    if (value > 0) {
      return value.toFixed(4);
    }
    return "0";
  };

  const formatNumber = (value) => {
    if (!value && value !== 0) return "0";
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3"><RefreshCw className="h-8 w-8 animate-spin text-accent" /><span className="text-muted-foreground">Loading analytics...</span></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
          <p className="text-muted-foreground">
            Real-time QPS metrics, performance analysis, and endpoint monitoring
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Activity className="h-4 w-4 mr-2" />}
            {dataLoaded ? "Refresh" : "Generate"}
          </Button>
        </div>
      </div>

      {!dataLoaded && (
        <Card className="py-16">
          <CardContent className="text-center text-muted-foreground">
            <Activity className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="text-lg font-medium">Select period and click "Generate"</p>
            <p className="text-sm mt-1">View QPS metrics, regional analysis, and endpoint performance</p>
          </CardContent>
        </Card>
      )}

      {dataLoaded && (<>

      {/* Live QPS Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                  Incoming QPS
                </p>
                <p className="text-3xl font-bold text-blue-600">
                  {formatQPS(liveQPS?.incoming_qps)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {liveQPS?.total_requests_last_minute || 0} req/min
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <ArrowDownRight className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                  Valid Incoming QPS
                </p>
                <p className="text-3xl font-bold text-green-600">
                  {formatQPS(liveQPS?.valid_incoming_qps)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {liveQPS?.fill_rate || 0}% fill rate
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <Activity className="h-6 w-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                  Outgoing QPS
                </p>
                <p className="text-3xl font-bold text-purple-600">
                  {formatQPS(liveQPS?.outgoing_qps)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">to DSP endpoints</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-500/10 flex items-center justify-center">
                <ArrowUpRight className="h-6 w-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                  Invalid QPS
                </p>
                <p className="text-3xl font-bold text-orange-600">
                  {formatQPS(liveQPS?.invalid_qps)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">filtered/rejected</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-500/10 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* QPS Timeseries Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            QPS Trends
          </CardTitle>
          <CardDescription>
            Queries per second over time - {period === "today" ? "Today" : period === "yesterday" ? "Yesterday" : period}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeseries}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey={period === "today" || period === "yesterday" ? "hour" : "date"} 
                  tick={{ fontSize: 12 }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: "hsl(var(--card))", 
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px"
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="incoming_qps" 
                  stroke="#3b82f6" 
                  name="Incoming QPS"
                  strokeWidth={2}
                  dot={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="valid_qps" 
                  stroke="#10b981" 
                  name="Valid QPS"
                  strokeWidth={2}
                  dot={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="outgoing_qps" 
                  stroke="#8b5cf6" 
                  name="Outgoing QPS"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Regional QPS & Tabs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Regional QPS */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Regional QPS
            </CardTitle>
            <CardDescription>Traffic distribution by region</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {regionalQPS.map((region, idx) => (
                <div key={region.region} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div 
                        className="h-3 w-3 rounded-full" 
                        style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                      />
                      <span className="font-medium">{region.region_name}</span>
                    </div>
                    <span className="text-sm font-mono">{formatQPS(region.incoming_qps)} QPS</span>
                  </div>
                  <div className="flex gap-2 text-xs text-muted-foreground">
                    <span>SSPs: {region.ssp_count}</span>
                    <span>|</span>
                    <span>DSPs: {region.dsp_count}</span>
                    <span>|</span>
                    <span>Latency: {region.latency_avg_ms}ms</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all"
                      style={{ 
                        width: `${Math.min((region.incoming_qps / (regionalQPS[0]?.incoming_qps || 1)) * 100, 100)}%`,
                        backgroundColor: COLORS[idx % COLORS.length]
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* SSP/DSP Performance Tabs */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Endpoint Performance</CardTitle>
            <CardDescription>QPS breakdown by SSP and DSP endpoints</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="ssp">
              <TabsList className="mb-4">
                <TabsTrigger value="ssp" className="gap-2">
                  <Server className="h-4 w-4" />
                  SSP Endpoints
                </TabsTrigger>
                <TabsTrigger value="dsp" className="gap-2">
                  <Radio className="h-4 w-4" />
                  DSP Endpoints
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="ssp">
                <div className="max-h-80 overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Endpoint</TableHead>
                        <TableHead className="text-right">Total Requests</TableHead>
                        <TableHead className="text-right">Valid</TableHead>
                        <TableHead className="text-right">Win Rate</TableHead>
                        <TableHead className="text-right">Latency</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sspQPS.slice(0, 10).map((ssp) => (
                        <TableRow key={ssp.endpoint_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{ssp.endpoint_name}</p>
                              <p className="text-xs text-muted-foreground">{ssp.company_name}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatNumber(ssp.total_requests)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-green-600">
                            {formatNumber(ssp.valid_requests)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant={ssp.win_rate > 30 ? "default" : "secondary"}>
                              {ssp.win_rate?.toFixed(1)}%
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {ssp.avg_latency_ms?.toFixed(0) || 0}ms
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
              
              <TabsContent value="dsp">
                <div className="max-h-80 overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Endpoint</TableHead>
                        <TableHead className="text-right">Out QPS</TableHead>
                        <TableHead className="text-right">Bid Rate</TableHead>
                        <TableHead className="text-right">QPS Util</TableHead>
                        <TableHead className="text-right">Latency</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dspQPS.slice(0, 10).map((dsp) => (
                        <TableRow key={dsp.endpoint_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{dsp.endpoint_name}</p>
                              <p className="text-xs text-muted-foreground">{dsp.company_name}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatQPS(dsp.outgoing_qps)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant={dsp.bid_rate > 50 ? "default" : "secondary"}>
                              {dsp.bid_rate}%
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={dsp.qps_utilization > 80 ? "text-orange-500" : ""}>
                              {dsp.qps_utilization}%
                            </span>
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {dsp.avg_latency_ms}ms
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Conflicts & Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Endpoint Conflicts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Endpoint Conflicts
            </CardTitle>
            <CardDescription>Detected issues and recommendations</CardDescription>
          </CardHeader>
          <CardContent>
            {conflicts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Zap className="h-8 w-8 mx-auto mb-2 text-green-500" />
                <p>No conflicts detected</p>
                <p className="text-sm">All endpoints are configured correctly</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-64 overflow-auto">
                {conflicts.map((conflict, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg border ${
                      conflict.severity === "warning" 
                        ? "bg-orange-500/10 border-orange-500/20" 
                        : "bg-blue-500/10 border-blue-500/20"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <AlertTriangle className={`h-4 w-4 mt-0.5 ${
                        conflict.severity === "warning" ? "text-orange-500" : "text-blue-500"
                      }`} />
                      <div>
                        <p className="font-medium text-sm">{conflict.type.replace(/_/g, " ").toUpperCase()}</p>
                        <p className="text-sm text-muted-foreground">{conflict.message}</p>
                        {conflict.endpoint_name && (
                          <Badge variant="outline" className="mt-1">{conflict.endpoint_name}</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <Card>
          <CardHeader>
            <CardTitle>Period Summary</CardTitle>
            <CardDescription>
              Aggregated statistics for {period === "today" ? "today" : period === "yesterday" ? "yesterday" : `last ${period}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {summary && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Total Requests</p>
                  <p className="text-2xl font-bold">{summary.total_requests?.toLocaleString()}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Valid Requests</p>
                  <p className="text-2xl font-bold text-green-600">{summary.total_valid_requests?.toLocaleString()}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Avg Incoming QPS</p>
                  <p className="text-2xl font-bold">{summary.avg_incoming_qps}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Fill Rate</p>
                  <p className="text-2xl font-bold text-blue-600">{summary.fill_rate}%</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Peak QPS</p>
                  <p className="text-2xl font-bold text-purple-600">{formatQPS(summary.peak_qps)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Peak Time</p>
                  <p className="text-2xl font-bold">{summary.peak_time}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Active SSPs</p>
                  <p className="text-2xl font-bold">{summary.active_ssps}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Active DSPs</p>
                  <p className="text-2xl font-bold">{summary.active_dsps}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      </>)}
    </div>
  );
}
